<template>
  <section class="store-picker" aria-labelledby="insights-store-picker-title">
    <div class="store-picker__heading">
      <div>
        <h2 id="insights-store-picker-title">Compare stores</h2>
        <p>{{ modelValue.length }} of {{ stores.length }} selected</p>
      </div>
      <div class="store-picker__actions">
        <button type="button" @click="selectAll" :disabled="allSelected">Select all authorized</button>
        <button type="button" @click="clearAll" :disabled="!modelValue.length">Unselect all</button>
      </div>
    </div>

    <label class="store-picker__search">
      <span class="sr-only">Search authorized stores</span>
      <hs-icon name="search" :size="16" aria-hidden="true" />
      <input v-model="search" type="search" placeholder="Search stores" />
    </label>

    <div v-if="groups.length" class="store-picker__groups" aria-label="Store group presets">
      <span>Presets</span>
      <button
        v-for="group in groups"
        :key="group.id || group.name"
        type="button"
        @click="$emit('apply-group', group)"
      >
        {{ group.name || group.group_name }}
      </button>
    </div>

    <div class="store-picker__list" role="group" aria-label="Authorized stores">
      <label v-for="store in filteredStores" :key="store.id" class="store-picker__option">
        <input
          type="checkbox"
          :checked="isSelected(store.id)"
          :disabled="!isSelected(store.id) && atLimit"
          @change="toggle(store.id)"
        />
        <span>
          <strong>{{ store.name }}</strong>
          <small>{{ store.currency || "Currency unavailable" }}</small>
        </span>
      </label>
      <p v-if="!filteredStores.length" class="store-picker__none">No authorized stores match your search.</p>
    </div>
    <p v-if="maxStores" class="store-picker__limit">Up to {{ maxStores }} stores per comparison.</p>
  </section>
</template>

<script>
import { HsIcon } from "@/components/ui";

export default {
  name: "InsightsStoreSelector",
  components: { HsIcon },
  props: {
    modelValue: { type: Array, default: () => [] },
    stores: { type: Array, default: () => [] },
    groups: { type: Array, default: () => [] },
    maxStores: { type: Number, default: 0 },
  },
  emits: ["update:modelValue", "apply-group"],
  data: () => ({ search: "" }),
  computed: {
    filteredStores() {
      const term = this.search.trim().toLowerCase();
      if (!term) return this.stores;
      return this.stores.filter((store) =>
        `${store.name} ${store.currency}`.toLowerCase().includes(term),
      );
    },
    allSelected() {
      const maximum = this.maxStores || this.stores.length;
      return (
        this.modelValue.length === Math.min(maximum, this.stores.length) &&
        this.modelValue.every((id) =>
          this.stores.some((store) => String(store.id) === String(id)),
        )
      );
    },
    atLimit() {
      return Boolean(this.maxStores && this.modelValue.length >= this.maxStores);
    },
  },
  methods: {
    isSelected(id) {
      return this.modelValue.map(String).includes(String(id));
    },
    toggle(id) {
      const value = String(id);
      const next = this.modelValue.map(String);
      const index = next.indexOf(value);
      if (index >= 0) next.splice(index, 1);
      else if (!this.atLimit) next.push(value);
      this.$emit("update:modelValue", next);
    },
    selectAll() {
      const maximum = this.maxStores || this.stores.length;
      this.$emit(
        "update:modelValue",
        this.stores.slice(0, maximum).map((store) => String(store.id)),
      );
    },
    clearAll() {
      this.$emit("update:modelValue", []);
    },
  },
};
</script>

<style scoped>
.store-picker { display: grid; gap: .9rem; }
.store-picker__heading { align-items: flex-start; display: flex; gap: 1rem; justify-content: space-between; }
.store-picker h2 { color: var(--hs-text, #0f172a); font-size: 1rem; font-weight: 900; margin: 0; }
.store-picker p { color: var(--hs-muted, #64748b); font-size: .78rem; margin: .2rem 0 0; }
.store-picker__actions, .store-picker__groups { display: flex; flex-wrap: wrap; gap: .45rem; }
.store-picker button { background: transparent; border: 1px solid var(--hs-border, #cbd5e1); border-radius: 999px; color: var(--hs-primary, #2563eb); cursor: pointer; font-size: .75rem; font-weight: 800; padding: .38rem .7rem; }
.store-picker button:disabled { cursor: not-allowed; opacity: .45; }
.store-picker__search { align-items: center; border: 1px solid var(--hs-border, #cbd5e1); border-radius: 12px; display: flex; gap: .5rem; padding: .65rem .75rem; }
.store-picker__search:focus-within { box-shadow: 0 0 0 3px color-mix(in srgb, var(--hs-primary, #3293ec) 15%, transparent); }
.store-picker__search input { background: transparent; border: 0; color: var(--hs-text, #0f172a); min-width: 0; outline: 0; width: 100%; }
.store-picker__groups { align-items: center; }
.store-picker__groups > span { color: var(--hs-muted, #64748b); font-size: .74rem; font-weight: 900; text-transform: uppercase; }
.store-picker__list { display: grid; gap: .45rem; grid-template-columns: repeat(auto-fill, minmax(170px, 1fr)); max-height: 210px; overflow: auto; }
.store-picker__option { align-items: center; border: 1px solid var(--hs-border, #e2e8f0); border-radius: 12px; cursor: pointer; display: flex; gap: .65rem; padding: .65rem; }
.store-picker__option:focus-within { outline: 2px solid var(--hs-primary, #3293ec); }
.store-picker__option input { accent-color: var(--hs-primary, #3293ec); height: 1rem; width: 1rem; }
.store-picker__option span { display: grid; min-width: 0; }
.store-picker__option strong { color: var(--hs-text, #0f172a); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.store-picker__option small, .store-picker__none, .store-picker__limit { color: var(--hs-muted, #64748b); }
.sr-only { height: 1px; margin: -1px; overflow: hidden; position: absolute; width: 1px; clip: rect(0, 0, 0, 0); }
@media (max-width: 640px) {
  .store-picker__heading { display: grid; }
}
</style>
